import React from "react";
import { useQueryOrganizations } from "src/api/organizations";
import { useQueryPointsOfSales } from "src/api/pointsOfSales";
import { useQueryUsers } from "src/api/users/query";
import { SearchType } from "src/components/generics/nestedSelect/NestedSelect";
import type { Organization } from "src/types/organization";
import type { Pos } from "src/types/pos";
import type { UserRelationIds } from "src/types/user";

interface SearchElement {
    id: number;
    label: string;
    value: Organization | Pos | UserRelationIds;
    type: SearchType;
    key: string;
}

interface SuperAdminSearchBarHook {
    elements: SearchElement[];
    isPending: boolean;
    handleChangeAdminSearchBar: (string) => void;
}

export const useSuperAdminSearchBar = (open: boolean): SuperAdminSearchBarHook => {
    const [searchText, handleChangeAdminSearchBar] = React.useState(null);

    const [options, setOptions] = React.useState({
        elements: [],
        isPending: false,
    });

    const search = open && searchText?.length > 2;

    const { data: organizations, isPending: organizationsPending } = useQueryOrganizations(
        { light: true },
        search
    );
    const { data: users, isPending: usersPending } = useQueryUsers(search, undefined, true);
    const { data: pointsOfSales, isPending: pointsOfSalesPending } = useQueryPointsOfSales({
        enabled: search,
        activatedOnly: true,
        relations: [],
    });

    const orgaLoading = open && !organizationsPending;
    const userLoading = open && !usersPending;
    const posLoading = open && !pointsOfSalesPending;

    React.useEffect(() => {
        if (orgaLoading || userLoading || posLoading) {
            const organizationsElements: SearchElement[] = (organizations || []).map(
                (organization) => ({
                    id: organization.id,
                    label: organization.name,
                    value: organization,
                    type: SearchType.Organization,
                    key: `orga-${organization.id}-${organization.name}`,
                })
            );
            const pointsOfSalesElements: SearchElement[] = (pointsOfSales || []).map(
                (pointOfSale) => ({
                    id: pointOfSale.id,
                    label: pointOfSale.name,
                    value: pointOfSale,
                    type: SearchType.PointOfSales,
                    key: `pos-${pointOfSale.id}-${pointOfSale.name}`,
                })
            );
            const usersElements: SearchElement[] = (users || []).map((user) => ({
                id: user.id,
                label: `${user.firstName} ${user.lastName}`,
                value: user,
                type: SearchType.User,
                key: `user-${user.id}-${user.firstName}-${user.lastName}`,
            }));

            setOptions({
                elements: [...organizationsElements, ...pointsOfSalesElements, ...usersElements],
                isPending: organizationsPending || pointsOfSalesPending || usersPending,
            });
        }
    }, [orgaLoading, userLoading, posLoading]);

    return { ...options, handleChangeAdminSearchBar };
};
